import * as Sentry from '@sentry/nuxt'

const { public: { sentryDsn } } = useRuntimeConfig()

Sentry.init({
  enabled: import.meta.env.PROD,
  dsn: sentryDsn,
  environment: import.meta.env.PROD ? 'production' : 'development',

  integrations: [
    Sentry.browserTracingIntegration(),
    // UI Profiling: amostra a thread principal do navegador p/ achar travadas de
    // render (Syncfusion DocumentEditor, AG Grid). Só coleta se a resposta HTML
    // for servida com o header `Document-Policy: js-profiling` (ver routeRules e o
    // servidor web de produção) — sem o header, falha silenciosamente.
    Sentry.browserProfilingIntegration()
    // Sistema de saúde: mascara todo texto e bloqueia mídia no replay para
    // não enviar dados de paciente (PII/PHI) ao Sentry (LGPD).
    // Sentry.replayIntegration({
    //   maskAllText: true,
    //   blockAllMedia: true
    // })
  ],

  // Descarta erros de conectividade transitórios (sem resposta HTTP). Eles já
  // são mostrados ao usuário via toast em useHttp e são ambientais, não bugs.
  beforeSend(event, hint) {
    const error = hint?.originalException as { isNetworkError?: boolean, statusCode?: number } | undefined
    if (error?.isNetworkError)
      return null
    // 401: sessão expirada — já tratado em useHttp (toast + signOut). Ruído, não bug.
    if (error?.statusCode === 401)
      return null
    // Chunk obsoleto após deploy: o plugin reload-on-stale-manifest já dispara reload
    // no hook app:chunkError. O erro em si é ambiental (cache do cliente x build novo),
    // não um bug — descarta pra não virar ruído no Sentry.
    const msg = (error as { message?: string } | undefined)?.message ?? ''
    if (/Failed to fetch dynamically imported module|Importing a module script failed/.test(msg))
      return null
    // Crashes internos do Syncfusion DocumentEditor (ej2): disparam dentro dos próprios
    // event handlers do editor (mousemove da régua, scroll, postMessage/zoom) quando a
    // árvore de widgets/seleção ainda não terminou de montar. Não afetam o usuário (0
    // impactados). O filtro por filename `@syncfusion` abaixo NÃO pega isto em produção,
    // pois o beforeSend roda no bundle minificado (sem source map) — os caminhos
    // @syncfusion e o flag in_app só aparecem depois que o Sentry aplica o source map no
    // servidor. Por isso filtramos pela mensagem: estes nomes de propriedade são internos
    // do editor e nunca aparecem como alvo de leitura no nosso código.
    if (/Cannot read properties of (?:undefined|null) \(reading '(?:paragraph|childWidgets|containerWidget|bodyWidget|sectionFormat|boundingRectangle|isShapeSelected|line|formFieldData)'\)/.test(msg))
      return null
    // Crash interno do AG Grid na navegação por teclado do cabeçalho
    // (HeaderNavigationService.findHeader → getColGroupAtLevel) quando o ponteiro de
    // grupo de coluna é null. 100% interno da lib, nunca chamamos getProvidedColumnGroup
    // no nosso código; mesmo motivo do filtro por mensagem acima (Sentry JAVASCRIPT-NUXT-1S).
    if (/Cannot read properties of (?:undefined|null) \(reading 'getProvidedColumnGroup'\)/.test(msg))
      return null
    // Crashes 100% internos de libs de terceiros (editor Syncfusion ej2, AG Grid),
    // disparados nos event handlers/efeitos delas. Não têm frame da nossa aplicação
    // (in_app) e não afetam o usuário (0 impactados) — ex.: reading 'page'/'paragraph'/
    // 'boundingRectangle' no documenteditor, 'getProvidedColumnGroup' no ag-grid.
    // Descarta pra reduzir ruído; se algum frame for nosso, o evento é mantido.
    const frames = event.exception?.values?.flatMap(v => v.stacktrace?.frames ?? []) ?? []
    if (frames.length) {
      const hasAppFrame = frames.some(f => f.in_app === true)
      const fromThirdPartyLib = frames.some(f =>
        typeof f.filename === 'string' && /@syncfusion|ag-grid/.test(f.filename))
      if (!hasAppFrame && fromThirdPartyLib)
        return null
    }
    return event
  },

  // Captura 10% das transações em produção para performance tracing
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

  // UI Profiling: relativo às transações já amostradas (tracesSampleRate).
  // 1.0 = perfila 100% das transações capturadas (= 10% do total em prod).
  profilesSampleRate: 1.0,

  // Grava replay apenas quando ocorre um erro
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0
})
